import type React from "react";
import { Skeleton } from "@/components/tailwind/ui/skeleton";

interface PageSkeletonProps {
  type?: "home" | "documents" | "document" | "help" | "subscription";
}

export const PageSkeleton: React.FC<PageSkeletonProps> = ({ type = "home" }) => {
  const renderHomeSkeleton = () => (
    <div className="h-full bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-black dark:via-neutral-800 dark:to-black">
      <div className="relative overflow-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="text-center">
            <Skeleton className="h-16 w-96 mx-auto mb-6" />
            <Skeleton className="h-8 w-3xl mx-auto mb-8" />
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Skeleton className="h-12 w-32" />
              <Skeleton className="h-12 w-32" />
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <Skeleton className="h-8 w-64 mx-auto mb-4" />
          <Skeleton className="h-6 w-96 mx-auto" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {["feature-1", "feature-2", "feature-3", "feature-4", "feature-5", "feature-6"].map((key) => (
            <div key={key} className="p-6 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <div className="flex items-center mb-4">
                <Skeleton className="h-10 w-10 rounded-lg mr-4" />
                <Skeleton className="h-6 w-32" />
              </div>
              <Skeleton className="h-4 w-full mb-2" />
              <Skeleton className="h-4 w-3/4" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderDocumentsSkeleton = () => (
    <div className="h-full bg-gray-50 dark:bg-card p-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div>
            <Skeleton className="h-8 w-48 mb-2" />
            <Skeleton className="h-5 w-64" />
          </div>
          <Skeleton className="h-10 w-32" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {["doc-1", "doc-2", "doc-3", "doc-4", "doc-5", "doc-6"].map((key) => (
            <div key={key} className="bg-white dark:bg-gray-700 rounded-lg p-6 shadow-sm">
              <Skeleton className="h-6 w-3/4 mb-3" />
              <Skeleton className="h-4 w-full mb-2" />
              <Skeleton className="h-4 w-2/3 mb-4" />
              <div className="flex justify-between items-center">
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-8 w-16" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderDocumentSkeleton = () => (
    <div className="h-full bg-white dark:bg-card">
      <div className="flex h-full">
        <div className="w-80 border-r border-gray-200 dark:border-gray-700 p-4">
          <Skeleton className="h-8 w-32 mb-4" />
          <div className="space-y-3">
            {["nav-1", "nav-2", "nav-3", "nav-4", "nav-5"].map((key) => (
              <div key={key} className="flex items-center space-x-3">
                <Skeleton className="h-4 w-4" />
                <Skeleton className="h-4 w-24" />
              </div>
            ))}
          </div>
        </div>

        <div className="flex-1 p-6">
          <Skeleton className="h-8 w-48 mb-6" />
          <div className="space-y-4">
            {["line-1", "line-2", "line-3", "line-4", "line-5", "line-6", "line-7", "line-8"].map((key) => (
              <Skeleton key={key} className="h-4 w-full" />
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const rendersubscriptionSkeleton = () => (
    <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header Skeleton */}
        <div className="mb-8">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <Skeleton className="h-9 w-9" />
                <Skeleton className="h-9 w-64" />
              </div>
              <Skeleton className="h-5 w-[600px]" />
            </div>
          </div>
        </div>

        {/* Admin Plan Management Section Skeleton (if admin) */}
        <div className="mb-12">
          <Skeleton className="h-8 w-48 mb-6" />

          {/* Included Plans */}
          <div className="mb-8">
            <Skeleton className="h-6 w-32 mb-4" />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => (
                <div key={`included-${i}`} className="relative">
                  <div className="absolute top-2 right-2 z-10">
                    <Skeleton className="w-8 h-8 rounded" />
                  </div>
                  <div className="bg-white dark:bg-card rounded-2xl shadow-lg border-2 border-gray-200 dark:border-border">
                    <div className="p-8">
                      <div className="text-center mb-6">
                        <Skeleton className="h-8 w-3/4 mx-auto mb-2" />
                        <Skeleton className="h-4 w-full" />
                      </div>
                      <div className="text-center mb-6">
                        <Skeleton className="h-16 w-24 mx-auto" />
                      </div>
                      <div className="space-y-3 mb-8">
                        {[1, 2, 3, 4].map((j) => (
                          <div key={j} className="flex items-center gap-3">
                            <Skeleton className="h-5 w-5 rounded" />
                            <Skeleton className="h-4 flex-1" />
                          </div>
                        ))}
                      </div>
                      <Skeleton className="h-12 w-full rounded-lg" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Available Stripe Plans */}
          <div>
            <Skeleton className="h-6 w-48 mb-4" />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => (
                <div key={`available-${i}`} className="relative">
                  <div className="absolute top-2 right-2 z-10">
                    <Skeleton className="w-8 h-8 rounded" />
                  </div>
                  <div className="bg-white dark:bg-card rounded-2xl shadow-lg border-2 border-gray-200 dark:border-border">
                    <div className="p-8">
                      <div className="text-center mb-6">
                        <Skeleton className="h-8 w-3/4 mx-auto mb-2" />
                        <Skeleton className="h-4 w-full" />
                      </div>
                      <div className="text-center mb-6">
                        <Skeleton className="h-16 w-24 mx-auto" />
                      </div>
                      <div className="space-y-3 mb-8">
                        {[1, 2, 3, 4].map((j) => (
                          <div key={j} className="flex items-center gap-3">
                            <Skeleton className="h-5 w-5 rounded" />
                            <Skeleton className="h-4 flex-1" />
                          </div>
                        ))}
                      </div>
                      <Skeleton className="h-12 w-full rounded-lg" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Regular User Plan Selection */}
        <>
          {/* Loading State */}
          <div className="text-center py-12 mb-20">
            <Skeleton className="h-6 w-48 mx-auto" />
          </div>

          {/* Billing Toggle */}
          <div className="flex justify-center mb-20">
            <div className="bg-white dark:bg-card rounded-lg p-1 border border-gray-200 dark:border-border">
              <Skeleton className="w-20 h-10 rounded-md" />
              <Skeleton className="w-20 h-10 rounded-md" />
            </div>
          </div>

          {/* Pricing Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className={`relative bg-white dark:bg-card rounded-2xl shadow-lg border-2 transition-all duration-200 hover:shadow-xl ${i === 2 ? "border-gray-900 dark:border-white" : "border-gray-200 dark:border-border"
                  }`}
              >
                {/* Badge for popular plan */}
                {i === 2 && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <Skeleton className="h-6 w-32 rounded-full" />
                  </div>
                )}

                <div className="p-8">
                  {/* Plan Header */}
                  <div className="text-center mb-6">
                    <Skeleton className="h-8 w-3/4 mx-auto mb-2" />
                    <Skeleton className="h-4 w-full" />
                  </div>

                  {/* Price */}
                  <div className="text-center mb-6">
                    <div className="flex items-baseline justify-center">
                      <Skeleton className="h-16 w-20" />
                      <Skeleton className="h-6 w-12 ml-1" />
                    </div>
                    <Skeleton className="h-4 w-24 mx-auto mt-1" />
                  </div>

                  {/* Features */}
                  <div className="space-y-3 mb-8">
                    {[1, 2, 3, 4].map((j) => (
                      <div key={j} className="flex items-center gap-3">
                        <Skeleton className="h-5 w-5 rounded" />
                        <Skeleton className={`${j === 1 ? "h-5" : "h-4"} flex-1`} />
                      </div>
                    ))}
                  </div>

                  {/* CTA Button */}
                  <Skeleton className="h-12 w-full rounded-lg" />
                </div>
              </div>
            ))}
          </div>
        </>
      </div>
    </div>
  );

  const renderGenericSkeleton = () => (
    <div className="h-full bg-gray-50 dark:bg-card p-6">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <Skeleton className="h-10 w-64 mx-auto mb-4" />
          <Skeleton className="h-6 w-96 mx-auto" />
        </div>

        <div className="space-y-6">
          {["section-1", "section-2", "section-3", "section-4"].map((key) => (
            <div key={key} className="bg-white dark:bg-gray-700 rounded-lg p-6">
              <Skeleton className="h-6 w-48 mb-3" />
              <Skeleton className="h-4 w-full mb-2" />
              <Skeleton className="h-4 w-3/4" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  switch (type) {
    case "home":
      return renderHomeSkeleton();
    case "documents":
      return renderDocumentsSkeleton();
    case "document":
      return renderDocumentSkeleton();
    case "subscription":
      return rendersubscriptionSkeleton();
    default:
      return renderGenericSkeleton();
  }
};
